/**
 * Mock Product Data
 *
 * Sample products for muscle food/supplements e-commerce
 */

import type { Product, ProductImage, NutritionInfo } from '../types/shopify.types';

interface MockMoney {
  amount: number;
  currencyCode: string;
  formatted: string;
}

const createImage = (id: string, url: string, alt?: string): ProductImage => ({
  id,
  url,
  altText: alt,
  width: 800,
  height: 800,
});

const createMoney = (amount: number): MockMoney => ({
  amount,
  currencyCode: 'EUR',
  formatted: new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount),
});

const nutritionWhey: NutritionInfo = {
  servingSize: '30g',
  servingsPerContainer: 73,
  calories: 120,
  protein: 24,
  carbohydrates: 3,
  fats: 1,
  fiber: 0,
  ingredients: 'Whey Protein Concentrate, Whey Protein Isolate, Natural Flavors, Sucralose.',
  allergens: ['Milk', 'Soy'],
};

const nutritionCreatine: NutritionInfo = {
  servingSize: '5g',
  servingsPerContainer: 200,
  calories: 0,
  protein: 0,
  carbohydrates: 0,
  fats: 0,
  ingredients: 'Creatine Monohydrate (micronized).',
  allergens: [],
};

const nutritionBCAA: NutritionInfo = {
  servingSize: '10g',
  servingsPerContainer: 60,
  calories: 40,
  protein: 5,
  carbohydrates: 0,
  fats: 0,
  ingredients: 'L-Leucine, L-Isoleucine, L-Valine (2:1:1 ratio).',
  allergens: [],
};

const nutritionPreWorkout: NutritionInfo = {
  servingSize: '15g',
  servingsPerContainer: 40,
  calories: 20,
  protein: 0,
  carbohydrates: 4,
  fats: 0,
  ingredients: 'Beta-Alanine, Caffeine, Citrulline Malate, Arginine, Natural Flavors.',
  allergens: [],
};

const nutritionMassGainer: NutritionInfo = {
  servingSize: '100g',
  servingsPerContainer: 10,
  calories: 390,
  protein: 30,
  carbohydrates: 50,
  fats: 9,
  fiber: 2,
  ingredients: 'Whey Protein, Maltodextrin, Creatine, MCT Oil, Natural Flavors.',
  allergens: ['Milk', 'Soy'],
};

export const MOCK_PRODUCTS: Product[] = [
  // Whey Protein
  {
    id: 'gid://shopify/Product/1',
    handle: 'whey-protein-isolate',
    title: 'Whey Protein Isolate - Cioccolato',
    description:
      'Whey Protein Isolate di alta qualità con oltre 90% di proteine per dose. Ideale per la crescita muscolare e il recupero post-allenamento.',
    descriptionHtml: `<p>Whey Protein Isolate di alta qualità con oltre 90% di proteine per dose.</p><ul><li>90% proteine per dose</li><li>Basso contenuto di grassi e carboidrati</li><li>Assorbimento rapido</li><li>Senza aspartamo</li></ul>`,
    productType: 'Proteine in polvere',
    vendor: 'MuscleFood',
    tags: ['proteine', 'whey', 'post-workout', 'cioccolato'],
    images: [
      createImage('img-1', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800', 'Whey Protein Cioccolato'),
      createImage('img-2', 'https://images.unsplash.com/photo-1594381898411-846e7d193883?w=800', 'Nutrition Facts'),
    ],
    featuredImage: createImage('img-1', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/1-1',
        title: '900g - Cioccolato',
        sku: 'WP-ISO-CHOC-900',
        availableForSale: true,
        price: createMoney(39.99),
        compareAtPrice: createMoney(49.99),
        weight: 900,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Peso', value: '900g' }, { name: 'Gusto', value: 'Cioccolato' }],
      },
      {
        id: 'gid://shopify/ProductVariant/1-2',
        title: '2.25kg - Cioccolato',
        sku: 'WP-ISO-CHOC-2250',
        availableForSale: true,
        price: createMoney(79.99),
        compareAtPrice: createMoney(99.99),
        weight: 2250,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Peso', value: '2.25kg' }, { name: 'Gusto', value: 'Cioccolato' }],
      },
    ],
    options: [
      { id: 'opt-1', name: 'Peso', values: ['900g', '2.25kg'] },
      { id: 'opt-2', name: 'Gusto', values: ['Cioccolato', 'Vaniglia', 'Frutti di bosco'] },
    ],
    priceRange: {
      min: createMoney(39.99),
      max: createMoney(79.99),
    },
    nutritionInfo: nutritionWhey,
    seo: {
      title: 'Whey Protein Isolate Cioccolato | MuscleFood',
      description: 'Whey Protein Isolate premium con 90% proteine. Gusto cioccolato, assorbimento rapido.',
    },
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-12-01T10:00:00Z',
  },

  // Creatine
  {
    id: 'gid://shopify/Product/2',
    handle: 'creatina-micronizzata',
    title: 'Creatina Monoidrato Micronizzata',
    description:
      'Creatina monoidrato di purezza 99.9% micronizzata per massimizzare l\'assorbimento. Aumenta forza, potenza e volume muscolare.',
    descriptionHtml: `<p>Creatina monoidrato di purezza 99.9% micronizzata.</p><ul><li>Purezza 99.9%</li><li>Micronizzata per assorbimento ottimale</li><li>Senza additivi</li><li>500g = 100 dosi</li></ul>`,
    productType: 'Creatina',
    vendor: 'MuscleFood',
    tags: ['creatina', 'forza', 'pre-workout', 'massa'],
    images: [
      createImage('img-3', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800', 'Creatina'),
    ],
    featuredImage: createImage('img-3', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/2-1',
        title: '500g',
        sku: 'CREA-MICRO-500',
        availableForSale: true,
        price: createMoney(19.99),
        weight: 500,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Peso', value: '500g' }],
      },
      {
        id: 'gid://shopify/ProductVariant/2-2',
        title: '1kg',
        sku: 'CREA-MICRO-1000',
        availableForSale: true,
        price: createMoney(34.99),
        compareAtPrice: createMoney(39.99),
        weight: 1000,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Peso', value: '1kg' }],
      },
    ],
    options: [{ id: 'opt-3', name: 'Peso', values: ['500g', '1kg'] }],
    priceRange: {
      min: createMoney(19.99),
      max: createMoney(34.99),
    },
    nutritionInfo: nutritionCreatine,
    seo: {
      title: 'Creatina Monoidrato Micronizzata | MuscleFood',
      description: 'Creatina pura 99.9% micronizzata. Aumenta forza e massa muscolare.',
    },
    createdAt: '2024-01-10T10:00:00Z',
    updatedAt: '2024-11-15T10:00:00Z',
  },

  // BCAA
  {
    id: 'gid://shopify/Product/3',
    handle: 'bcaa-2-1-1',
    title: 'BCAA 2:1:1 - 600 Compresse',
    description:
      'Aminoacidi ramificati in rapporto 2:1:1 (Leucina:Isoleucina:Valina). 600 compresse per 4 mesi di integrazione.',
    descriptionHtml: `<p>BCAA in rapporto ottimale 2:1:1.</p><ul><li>600 compresse</li><li>Rapporto 2:1:1</li><li>Purezza 100%</li><li>Tablettazione facile da deglutire</li></ul>`,
    productType: 'Aminoacidi',
    vendor: 'MuscleFood',
    tags: ['bcaa', 'aminoacidi', 'recupero', 'anti-catabolico'],
    images: [
      createImage('img-4', 'https://images.unsplash.com/photo-1621072156002-e2fccdc0b176?w=800', 'BCAA Compresse'),
    ],
    featuredImage: createImage('img-4', 'https://images.unsplash.com/photo-1621072156002-e2fccdc0b176?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/3-1',
        title: '600 compresse',
        sku: 'BCAA-211-600',
        availableForSale: true,
        price: createMoney(29.99),
        weight: 600,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Formato', value: '600 compresse' }],
      },
    ],
    options: [{ id: 'opt-4', name: 'Formato', values: ['600 compresse'] }],
    priceRange: {
      min: createMoney(29.99),
      max: createMoney(29.99),
    },
    nutritionInfo: nutritionBCAA,
    seo: {
      title: 'BCAA 2:1:1 Compresse | MuscleFood',
      description: 'Aminoacidi ramificati 600 compresse. Recupero muscolare ottimale.',
    },
    createdAt: '2024-01-20T10:00:00Z',
    updatedAt: '2024-11-20T10:00:00Z',
  },

  // Pre-Workout
  {
    id: 'gid://shopify/Product/4',
    handle: 'pre-workout-extreme',
    title: 'Pre-Workout Extreme - Energia e Focus',
    description:
      'Pre-workout potente con 300mg caffeina, beta-alanina e citrullina. Energia esplosiva e vasodilatazione per allenamenti intensi.',
    descriptionHtml: `<p>Pre-workout ad alta efficacia.</p><ul><li>300mg caffeina</li><li>3.2g beta-alanina</li><li>6g citrullina malato</li><li>Senza creatina</li></ul>`,
    productType: 'Pre-workout',
    vendor: 'MuscleFood',
    tags: ['pre-workout', 'energia', 'pump', 'focus'],
    images: [
      createImage('img-5', 'https://images.unsplash.com/photo-1579722821273-0f6c7d44362f?w=800', 'Pre-Workout'),
    ],
    featuredImage: createImage('img-5', 'https://images.unsplash.com/photo-1579722821273-0f6c7d44362f?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/4-1',
        title: '600g - Anguria',
        sku: 'PW-EXT-WM-600',
        availableForSale: true,
        price: createMoney(34.99),
        weight: 600,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Gusto', value: 'Anguria' }],
      },
      {
        id: 'gid://shopify/ProductVariant/4-2',
        title: '600g - Frutto della passione',
        sku: 'PW-EXT-PF-600',
        availableForSale: true,
        price: createMoney(34.99),
        weight: 600,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Gusto', value: 'Frutto della passione' }],
      },
    ],
    options: [{ id: 'opt-5', name: 'Gusto', values: ['Anguria', 'Frutto della passione', 'Cola'] }],
    priceRange: {
      min: createMoney(34.99),
      max: createMoney(34.99),
    },
    nutritionInfo: nutritionPreWorkout,
    seo: {
      title: 'Pre-Workout Extreme | MuscleFood',
      description: 'Pre-workout con caffeina, beta-alanina e citrullina. Energia e pump.',
    },
    createdAt: '2024-02-01T10:00:00Z',
    updatedAt: '2024-12-01T10:00:00Z',
  },

  // Mass Gainer
  {
    id: 'gid://shopify/Product/5',
    handle: 'mass-gainer-pro',
    title: 'Mass Gainer Pro - 3kg',
    description:
      'Mass gainer con 30g proteine e 390 calorie per dose. Ideale per chi cerca di aumentare peso e massa muscolare.',
    descriptionHtml: `<p>Mass gainer ad alto contenuto calorico.</p><ul><li>390 calorie per dose</li><li>30g proteine</li><li>50g carboidrati complessi</li><li>Con creatina</li></ul>`,
    productType: 'Mass Gainer',
    vendor: 'MuscleFood',
    tags: ['mass-gainer', 'massa', 'bulking', 'calorie'],
    images: [
      createImage('img-6', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800', 'Mass Gainer'),
    ],
    featuredImage: createImage('img-6', 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/5-1',
        title: '3kg - Cioccolato',
        sku: 'MG-PRO-CHOC-3000',
        availableForSale: true,
        price: createMoney(49.99),
        compareAtPrice: createMoney(59.99),
        weight: 3000,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Gusto', value: 'Cioccolato' }],
      },
      {
        id: 'gid://shopify/ProductVariant/5-2',
        title: '3kg - Vaniglia',
        sku: 'MG-PRO-VAN-3000',
        availableForSale: false,
        price: createMoney(49.99),
        weight: 3000,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Gusto', value: 'Vaniglia' }],
      },
    ],
    options: [{ id: 'opt-6', name: 'Gusto', values: ['Cioccolato', 'Vaniglia'] }],
    priceRange: {
      min: createMoney(49.99),
      max: createMoney(49.99),
    },
    nutritionInfo: nutritionMassGainer,
    seo: {
      title: 'Mass Gainer Pro 3kg | MuscleFood',
      description: 'Mass gainer con 30g proteine e 390 calorie per dose.',
    },
    createdAt: '2024-01-25T10:00:00Z',
    updatedAt: '2024-11-25T10:00:00Z',
  },

  // Omega-3
  {
    id: 'gid://shopify/Product/6',
    handle: 'omega-3-ultra',
    title: 'Omega-3 Ultra - 180 Capsule',
    description:
      'Integratore di Omega-3 ad alta concentrazione. EPA e DHA per salute cardiovascolare, funzione cerebrale e riduzione infiammazioni.',
    descriptionHtml: `<p>Omega-3 ultra concentrato.</p><ul><li>180 capsule (3 mesi)</li><li>1000mg per capsula</li><li>Alto EPA/DHA</li><li>Purezza testata</li></ul>`,
    productType: 'Omega-3',
    vendor: 'MuscleFood',
    tags: ['omega-3', 'salute', 'cardiovascolare', 'capsule'],
    images: [
      createImage('img-7', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800', 'Omega-3'),
    ],
    featuredImage: createImage('img-7', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/6-1',
        title: '180 capsule',
        sku: 'OMG3-ULTRA-180',
        availableForSale: true,
        price: createMoney(24.99),
        weight: 200,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Formato', value: '180 capsule' }],
      },
    ],
    options: [{ id: 'opt-7', name: 'Formato', values: ['180 capsule'] }],
    priceRange: {
      min: createMoney(24.99),
      max: createMoney(24.99),
    },
    seo: {
      title: 'Omega-3 Ultra 180 capsule | MuscleFood',
      description: 'Omega-3 ad alta concentrazione con EPA e DHA.',
    },
    createdAt: '2024-01-12T10:00:00Z',
    updatedAt: '2024-10-15T10:00:00Z',
  },

  // Vitamina D3
  {
    id: 'gid://shopify/Product/7',
    handle: 'vitamina-d3-5000iu',
    title: 'Vitamina D3 - 5000 IU - 180 Compresse',
    description:
      'Vitamina D3 ad alta dose per supportare la salute delle ossa, il sistema immunitario e la funzione muscolare.',
    descriptionHtml: `<p>Vitamina D3 5000 IU per dose.</p><ul><li>180 compresse (6 mesi)</li><li>5000 IU per compressa</li><li>Assorbimento ottimale</li></ul>`,
    productType: 'Vitamine',
    vendor: 'MuscleFood',
    tags: ['vitamina-d', 'salute', 'ossa', 'immunità'],
    images: [
      createImage('img-8', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800', 'Vitamina D3'),
    ],
    featuredImage: createImage('img-8', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/7-1',
        title: '180 compresse',
        sku: 'VITD3-5000-180',
        availableForSale: true,
        price: createMoney(14.99),
        weight: 100,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Formato', value: '180 compresse' }],
      },
    ],
    options: [{ id: 'opt-8', name: 'Formato', values: ['180 compresse'] }],
    priceRange: {
      min: createMoney(14.99),
      max: createMoney(14.99),
    },
    seo: {
      title: 'Vitamina D3 5000 IU | MuscleFood',
      description: 'Vitamina D3 ad alta dose per ossa e immunità.',
    },
    createdAt: '2024-01-08T10:00:00Z',
    updatedAt: '2024-09-20T10:00:00Z',
  },

  // ZMA
  {
    id: 'gid://shopify/Product/8',
    handle: 'zma-recupero-notturno',
    title: 'ZMA - Recupero Notturno - 90 Capsule',
    description:
      'Formula di Zinco, Magnesio e Vitamina B6 per ottimizzare il recupero notturno e migliorare la qualità del sonno.',
    descriptionHtml: `<p>ZMA per recupero ottimale.</p><ul><li>90 capsule (3 mesi)</li><li>Zinco monometionina</li><li>Magnesio aspartato</li><li>Vitamina B6</li></ul>`,
    productType: 'ZMA',
    vendor: 'MuscleFood',
    tags: ['zma', 'recupero', 'sonno', 'zinco', 'magnesio'],
    images: [
      createImage('img-9', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800', 'ZMA'),
    ],
    featuredImage: createImage('img-9', 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800'),
    variants: [
      {
        id: 'gid://shopify/ProductVariant/8-1',
        title: '90 capsule',
        sku: 'ZMA-90',
        availableForSale: true,
        price: createMoney(19.99),
        weight: 150,
        weightUnit: 'GRAMS',
        requiresShipping: true,
        selectedOptions: [{ name: 'Formato', value: '90 capsule' }],
      },
    ],
    options: [{ id: 'opt-9', name: 'Formato', values: ['90 capsule'] }],
    priceRange: {
      min: createMoney(19.99),
      max: createMoney(19.99),
    },
    seo: {
      title: 'ZMA Recupero Notturno | MuscleFood',
      description: 'Zinco, Magnesio e B6 per recupero e sonno.',
    },
    createdAt: '2024-01-18T10:00:00Z',
    updatedAt: '2024-10-05T10:00:00Z',
  },
];

// Helper function to find product by handle
export function findProductByHandle(handle: string): Product | undefined {
  return MOCK_PRODUCTS.find((p) => p.handle === handle);
}

// Helper function to find product by ID
export function findProductById(id: string): Product | undefined {
  return MOCK_PRODUCTS.find((p) => p.id === id);
}

// Helper function to get products by tag
export function getProductsByTag(tag: string): Product[] {
  return MOCK_PRODUCTS.filter((p) => p.tags.includes(tag));
}

// Helper function to get products by type
export function getProductsByType(productType: string): Product[] {
  return MOCK_PRODUCTS.filter((p) => p.productType === productType);
}

// Helper function to search products
export function searchProducts(query: string): Product[] {
  const lowerQuery = query.toLowerCase();
  return MOCK_PRODUCTS.filter(
    (p) =>
      p.title.toLowerCase().includes(lowerQuery) ||
      p.description.toLowerCase().includes(lowerQuery) ||
      p.tags.some((t) => t.toLowerCase().includes(lowerQuery))
  );
}
